import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Spacing from "../Spacing";
import { calculateAgeFromDOB, getInitials, getSecureData, removeSecureItem } from "../../helpers";
import { fetchGeneratePatientsData } from "../../services/patient";
import { useNavigate } from "react-router-dom";
import { getPendingQmData, setPendingQmData } from "../../Session";

export default function GenerateParientReport() {
  const navigate = useNavigate();
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [signOutPatientData, setSignOutPatientData] = useState([]);
  const contentRef = useRef();
  const [incomingData, setIncomingData] = useState(getPendingQmData());

  useEffect(() => {
    if(!incomingData) {
      navigate(`/waiting-room`);
    }
    getSignOutPatient(incomingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingData]);

  const getSignOutPatient = async (incomingData) => {    
    if (Object.keys(incomingData)?.length === 0) {      
      const getIncomingData = await getSecureData("pending_qm_data");
      setIncomingData(getIncomingData);
      return;
    }
    
    try {
      const response = await fetchGeneratePatientsData(incomingData);
      if (response) {
        if(response?.data?.results === null) {
          await removeSecureItem("pending_qm_data");
          setPendingQmData({});
          navigate(`/waiting-room`);
        }
        setSignOutPatientData(response?.data?.results);
      }
    } catch (error) {
      await removeSecureItem("pending_qm_data");
      setPendingQmData({});
      navigate(`/waiting-room`);
    } finally {
      setIsDataLoaded(true);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCloseWindow =async () => {
    window.close();
    await removeSecureItem("pending_qm_data");
    setPendingQmData({});
  };

  const getFormattedDate = () => {
    return new Date(Date.now()).toISOString().split("T")[0].replace(/-/g, "");
  };

  const downloadPdf = () => {
    if (isDataLoaded) {
      const content = contentRef.current;
      html2canvas(content).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }
        let fileName = `${getFormattedDate()}-QM-Summary-Report${incomingData?.selected_provider ? ` (${incomingData?.selected_provider})` : ''}.pdf`;
        pdf.save(fileName);
      });
    }
  };

  useDocumentTitle("Pending Quality Measures");

  return (
    <div ref={contentRef} className="docpro_signout_wrap">
      <div className="docpro_signout relative" style={{ minHeight: "100vh" }}>
        <h3 className="docpro_fs_20 text-center">Pending Quality Measures Summary</h3>
        {isDataLoaded ? (
          signOutPatientData?.length > 0 ? (
            signOutPatientData?.map((item, index) => {

              const isLastItem = index === signOutPatientData?.length - 1;

              return (
                <div key={index}>
                  <div className="docpro_signout_header" style={{ borderBottom: isLastItem && "none" }}>
                    <div className="w-full">
                      <div className="flex" style={{ justifyContent: 'space-between' }}>
                        <div>
                          <div className="flex" style={{ alignItems: 'start' }}>
                            <div className="docpro_header_avatar mr-2">
                              {getInitials(`${item?.first_name} ${item?.last_name}`)}{" "}
                            </div>
                            <div>
                              <h2 className="docpro_mb_0 docpro_fs_16 mb-0">
                                {item && item.last_name && item.first_name ? `${item.first_name} ${item.last_name}` : "" }
                              </h2>
                              {item && item?.risk_level === "High" && (
                                <span className="docpro_badge docpro_badge_red docpro_fs_12">
                                  High-Risk Patient
                                </span>
                              )}
                              {item && item?.risk_level === "Moderate" && (
                                <span className="docpro_badge docpro_badge_orange docpro_fs_12">
                                  Moderate-Risk Patient
                                </span>
                              )}
                              {item && item?.risk_level === "Low" && (
                                <span className="docpro_badge docpro_badge_green docpro_fs_12">
                                  Low-Risk Patient
                                </span>
                              )}
                            </div>
                          </div>
                          <p className="docpro_mb_0 docpro_fs_14 mt-2"><b>Last Visit: </b>{item && item?.last_visit ? item?.last_visit : ""}</p>
                          <Spacing lg="7" sm="7" />
                          <h4 className="docpro_mb_0 docpro_fs_14 mb-1">Pending QM: </h4>
                        </div>

                        <div className="pl-2" style={{ minWidth: '52%' }}>
                          <p className="docpro_mb_0 docpro_fs_14 mb-1"><b>Gender: </b>{item && item.sex ? item.sex : ""}</p>
                          <p className="docpro_mb_0 docpro_fs_14 mb-1"><b>DOB: </b>{item && item.dob ? `${item.dob} (${calculateAgeFromDOB(item.dob)} years old)` : ""}</p>
                          <p className="docpro_mb_0 docpro_fs_14 mb-1"><b>Insurance: </b>{item && item.insurance_name ? item.insurance_name : ""}</p>
                          <p className="docpro_mb_0 docpro_fs_14 mb-1"><b>Last annual wellness visit: </b>{item && item.last_annual_wellness_visit ? item.last_annual_wellness_visit : ""}</p>
                        </div>
                      </div>
                      <div>
                        {item?.pending_qm && item?.pending_qm?.length > 0 && (
                          <div className="label_wrapper">
                            <label className="docpro_fw_400 docpro_primary_color docpro_lh_18">
                              {item?.pending_qm?.map((qm_item, index2) => (
                                <span key={index2} style={{ marginRight: '4px' }}>
                                  {qm_item}
                                  {index2 !== item?.pending_qm?.length - 1 && ", "}
                                </span>
                              ))}
                            </label>
                          </div>
                        )}
                      </div>
                      <Spacing lg="15" sm="15" />
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="notification_dropdown_list custom-scroll">
              <div className="notification_dropdown_no_data text-center">No data available</div>
            </div>
          )
        ) : (
          <div className="notification_dropdown_list custom-scroll">
            <div className="notification_dropdown_no_data text-center">Loading...</div>
          </div>
        )}
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={handleCloseWindow}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x">
              <path d="M18 6 6 18"/>
              <path d="m6 6 12 12"/>
            </svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        <button
          className="docpro_invoice_btn docpro_color1"
          onClick={handlePrint}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512">
              <path d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H104a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
              <rect x={128} y={240} width={256} height={208} rx="24.32" ry="24.32" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
              <path d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth={32} />
              <circle cx={392} cy={184} r={24} fill="currentColor" />
            </svg>
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
        <button
          id="docpro_download_btn"
          className="docpro_invoice_btn docpro_color2"
          onClick={downloadPdf}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" className="ionicon" viewBox="0 0 512 512">
              <path d="M320 336h76c55 0 100-21.21 100-75.6s-53-73.47-96-75.6C391.11 99.74 329 48 256 48c-69 0-113.44 45.79-128 91.2-60 5.7-112 35.88-112 98.4S70 336 136 336h56M192 400.1l64 63.9 64-63.9M256 224v224.03" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={32} />
            </svg>
          </span>
          <span className="docpro_btn_text">Download</span>
        </button>
      </div>
    </div>
  );
}
